import { TaskResponse, GlobalTaskResponse, apiClient } from '../lib/api';
import ConfirmationModal from '../components/ConfirmationModal';
import RunLarsStagesModal from '../components/RunLarsStagesModal';
import GlobalTaskRunFailuresModal from '../components/GlobalTaskRunFailuresModal';
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { PlusIcon, TrashIcon, PlayIcon, AlertTriangleIcon, AlertCircleIcon } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

type TabId = 'tenant' | 'global';

function formatScheduleName(name: string) {
    return name.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function latestRunStatusBadge(status: string | null | undefined) {
    if (!status) {
        return <span className="text-sm text-gray-400">No runs</span>;
    }
    const colors: Record<string, string> = {
        completed: 'bg-green-100 text-green-800',
        success: 'bg-green-100 text-green-800',
        failed: 'bg-red-100 text-red-800',
        partial: 'bg-amber-100 text-amber-800',
        running: 'bg-blue-100 text-blue-800',
        pending: 'bg-gray-100 text-gray-800',
    };
    return (
        <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${colors[status] || 'bg-gray-100 text-gray-800'}`}>
            {status}
        </span>
    );
}

function hasFailedLatestRun(task: GlobalTaskResponse) {
    return (
        task.latest_run_status === 'failed'
        || task.latest_run_status === 'partial'
        || (task.latest_run_failed_count ?? 0) > 0
    );
}

export default function Tasks() {
    const [activeTab, setActiveTab] = useState<TabId>('tenant');
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [deleteGlobalModalOpen, setDeleteGlobalModalOpen] = useState(false);
    const [selectedTask, setSelectedTask] = useState<TaskResponse | null>(null);
    const [selectedGlobalTask, setSelectedGlobalTask] = useState<GlobalTaskResponse | null>(null);
    const [runningTaskId, setRunningTaskId] = useState<string | null>(null);
    const [runningGlobalTaskId, setRunningGlobalTaskId] = useState<string | null>(null);
    const [larsRunModalOpen, setLarsRunModalOpen] = useState(false);
    const [pendingLarsTask, setPendingLarsTask] = useState<GlobalTaskResponse | null>(null);
    const [failuresModalTask, setFailuresModalTask] = useState<GlobalTaskResponse | null>(null);
    const queryClient = useQueryClient();
    const [isLoading] = useState(false);
    const navigate = useNavigate();

    const { data: tasks = [], isLoading: isLoadingTasks } = useQuery<TaskResponse[]>({
        queryKey: ['tasks'],
        queryFn: apiClient.getTasks,
    });

    const { data: globalTasks = [], isLoading: isLoadingGlobalTasks } = useQuery<GlobalTaskResponse[]>({
        queryKey: ['global-tasks'],
        queryFn: apiClient.getGlobalTasks,
        // v4 signature: (data) => number | false
        refetchInterval: (data) =>
            data?.some((t) => t.latest_run_status === 'running' || t.latest_run_status === 'pending')
                ? 5000
                : false,
    });

    const tenantTasks = tasks.filter((t) => !t.task_extra_data?.isAllTenant);

    const handleDeleteTask = (task: TaskResponse) => {
        setSelectedTask(task);
        setDeleteModalOpen(true);
    };

    const confirmDeleteTask = async () => {
        if (selectedTask) {
            const response = await apiClient.deleteTasks(selectedTask.tenant_id!, selectedTask.id);
            setDeleteModalOpen(false);
            if (response.success) {
                queryClient.invalidateQueries({ queryKey: ['tasks'] });
                setError(null);
            } else {
                setError(response.message);
            }
            setSelectedTask(null);
        }
    };

    const handleDeleteGlobalTask = (task: GlobalTaskResponse) => {
        setSelectedGlobalTask(task);
        setDeleteGlobalModalOpen(true);
    };

    const confirmDeleteGlobalTask = async () => {
        if (selectedGlobalTask) {
            const response = await apiClient.deleteGlobalTask(selectedGlobalTask.id);
            setDeleteGlobalModalOpen(false);
            if (response.success) {
                queryClient.invalidateQueries({ queryKey: ['global-tasks'] });
            } else {
                setError(response.message);
            }
            setSelectedGlobalTask(null);
        }
    };

    const handleRunTaskNow = async (task: TaskResponse) => {
        setRunningTaskId(task.id);
        setError(null);
        setSuccess(null);
        try {
            const response = await apiClient.runTaskNow(task.id);
            if (response.status === "success" || response.success || response.execution_status === "executed" || response.execution_status === "initiated") {
                setSuccess(`Task "${task.schedule_name}" execution initiated.`);
                queryClient.invalidateQueries({ queryKey: ['tasks'] });
            } else {
                setError(response.message || 'Task execution failed');
            }
        } catch (err) {
            setError(`Failed to run task: ${err instanceof Error ? err.message : 'Unknown error'}`);
        } finally {
            setRunningTaskId(null);
        }
    };

    const handleRunGlobalTaskNow = async (task: GlobalTaskResponse) => {
        if (task.schedule_name === 'run_lars_stages') {
            setPendingLarsTask(task);
            setLarsRunModalOpen(true);
            return;
        }
        await executeGlobalTask(task);
    };

    const executeGlobalTask = async (task: GlobalTaskResponse, stages?: number[]) => {
        setRunningGlobalTaskId(task.id);
        setError(null);
        setSuccess(null);
        try {
            const response = await apiClient.runGlobalTaskNow(task.id, { stages });
            if (response.status === "success" || response.execution_status === "initiated") {
                const stageNote = stages?.length ? ` (stages ${stages.join(', ')})` : '';
                setSuccess(`Global task "${formatScheduleName(task.schedule_name)}" execution initiated${stageNote}.`);
                queryClient.invalidateQueries({ queryKey: ['global-tasks'] });
                // Poll list so latest_run_status flips from running → failed/completed
                setTimeout(() => queryClient.invalidateQueries({ queryKey: ['global-tasks'] }), 5000);
                setTimeout(() => queryClient.invalidateQueries({ queryKey: ['global-tasks'] }), 15000);
            } else {
                setError(response.message || 'Global task execution failed');
            }
        } catch (err) {
            setError(`Failed to run global task: ${err instanceof Error ? err.message : 'Unknown error'}`);
        } finally {
            setRunningGlobalTaskId(null);
            setLarsRunModalOpen(false);
            setPendingLarsTask(null);
        }
    };

    const isLoadingPage = activeTab === 'tenant' ? isLoadingTasks : isLoadingGlobalTasks;

    if (isLoadingPage && activeTab === 'tenant' && isLoadingTasks) {
        return <div className="p-4">Loading tasks...</div>;
    }

    return (
        <div>
            <div className="sm:flex sm:items-center">
                <div className="sm:flex-auto">
                    <h1 className="text-2xl font-bold leading-7 text-gray-900 sm:truncate sm:text-3xl sm:tracking-tight">
                        Tasks
                    </h1>
                    <p className="mt-2 text-sm text-gray-700">
                        Manage tenant and global background tasks.
                    </p>
                </div>
                <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
                    <button
                        type="button"
                        onClick={() => navigate(activeTab === 'tenant' ? '/tasks/create' : '/tasks/global/create')}
                        className="inline-flex items-center justify-center rounded-md border border-transparent bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700"
                    >
                        <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
                        {activeTab === 'tenant' ? 'New Task' : 'New Global Task'}
                    </button>
                </div>
            </div>

            <div className="mt-6 border-b border-gray-200">
                <nav className="-mb-px flex space-x-8">
                    <button
                        type="button"
                        onClick={() => setActiveTab('tenant')}
                        className={`py-4 px-1 border-b-2 font-medium text-sm ${
                            activeTab === 'tenant'
                                ? 'border-primary-500 text-primary-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                    >
                        Tenant
                    </button>
                    <button
                        type="button"
                        onClick={() => setActiveTab('global')}
                        className={`py-4 px-1 border-b-2 font-medium text-sm ${
                            activeTab === 'global'
                                ? 'border-primary-500 text-primary-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                    >
                        Global
                    </button>
                </nav>
            </div>

            {error && <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 mt-4">{error}</div>}
            {success && <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4 mt-4">{success}</div>}

            <div className="overflow-x-auto mt-4">
                {activeTab === 'tenant' ? (
                    <table className="min-w-full divide-y divide-gray-300">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tenant</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Task</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Frequency</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Run</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Next Run</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                                <th className="relative px-6 py-3"><span className="sr-only">Actions</span></th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {tenantTasks.map((task) => (
                                <tr key={task.id}>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{task.tenant_name}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{task.schedule_name}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{task.frequency}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm">{task.last_run ? new Date(task.last_run).toLocaleDateString() : 'N/A'}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{task.next_run ? new Date(task.next_run).toLocaleDateString() : 'N/A'}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{task.created_at ? new Date(task.created_at).toLocaleDateString() : 'N/A'}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                        <div className="flex items-center justify-end space-x-2">
                                            <button onClick={() => handleRunTaskNow(task)} disabled={runningTaskId === task.id} className="text-green-600 hover:text-green-900 disabled:opacity-50" title="Run now">
                                                {runningTaskId === task.id ? <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-green-600" /> : <PlayIcon className="h-5 w-5" />}
                                            </button>
                                            <button onClick={() => handleDeleteTask(task)} disabled={isLoading} className="text-red-600 hover:text-red-900" title="Delete">
                                                <TrashIcon className="h-5 w-5" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {tenantTasks.length === 0 && (
                                <tr><td colSpan={7} className="px-6 py-4 text-center text-sm text-gray-500">No tenant tasks found.</td></tr>
                            )}
                        </tbody>
                    </table>
                ) : (
                    <table className="min-w-full divide-y divide-gray-300">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Task</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Frequency</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tenants</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Run Status</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Run</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Next Run</th>
                                <th className="relative px-6 py-3"><span className="sr-only">Actions</span></th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {globalTasks.map((task) => (
                                <tr
                                    key={task.id}
                                    className="hover:bg-gray-50 cursor-pointer"
                                    onClick={(e) => {
                                        if ((e.target as HTMLElement).closest('button')) return;
                                        navigate(`/tasks/global/${task.id}`);
                                    }}
                                >
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-primary-700">{formatScheduleName(task.schedule_name)}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{task.frequency}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {task.active_member_count ?? 0} / {task.member_count ?? 0}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm" onClick={(e) => e.stopPropagation()}>
                                        <div className="flex items-center gap-2">
                                            {latestRunStatusBadge(task.latest_run_status)}
                                            {hasFailedLatestRun(task) && (
                                                <button
                                                    type="button"
                                                    onClick={() => setFailuresModalTask(task)}
                                                    className="text-red-600 hover:text-red-800"
                                                    title={
                                                        task.latest_run_failed_count
                                                            ? `View ${task.latest_run_failed_count} failed tenant run(s)`
                                                            : 'View failed run details'
                                                    }
                                                >
                                                    {task.latest_run_status === 'partial' ? (
                                                        <AlertCircleIcon className="h-5 w-5" />
                                                    ) : (
                                                        <AlertTriangleIcon className="h-5 w-5" />
                                                    )}
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm">{task.last_run ? new Date(task.last_run).toLocaleDateString() : 'N/A'}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{task.next_run ? new Date(task.next_run).toLocaleDateString() : 'N/A'}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" onClick={(e) => e.stopPropagation()}>
                                        <div className="flex items-center justify-end space-x-2">
                                            <button onClick={() => handleRunGlobalTaskNow(task)} disabled={runningGlobalTaskId === task.id} className="text-green-600 hover:text-green-900 disabled:opacity-50" title="Run all now">
                                                {runningGlobalTaskId === task.id ? <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-green-600" /> : <PlayIcon className="h-5 w-5" />}
                                            </button>
                                            <button onClick={() => handleDeleteGlobalTask(task)} className="text-red-600 hover:text-red-900" title="Delete">
                                                <TrashIcon className="h-5 w-5" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {globalTasks.length === 0 && !isLoadingGlobalTasks && (
                                <tr><td colSpan={7} className="px-6 py-4 text-center text-sm text-gray-500">No global tasks found.</td></tr>
                            )}
                        </tbody>
                    </table>
                )}
            </div>

            <ConfirmationModal isOpen={deleteModalOpen} onClose={() => setDeleteModalOpen(false)} onConfirm={confirmDeleteTask} title="Delete Task" message="Are you sure you want to delete this task?" confirmText="Delete Task" type="danger" isLoading={isLoading} />
            <ConfirmationModal isOpen={deleteGlobalModalOpen} onClose={() => setDeleteGlobalModalOpen(false)} onConfirm={confirmDeleteGlobalTask} title="Delete Global Task" message="Are you sure you want to delete this global task?" confirmText="Delete" type="danger" isLoading={isLoading} />


            <GlobalTaskRunFailuresModal
                isOpen={!!failuresModalTask}
                task={failuresModalTask}
                onClose={() => setFailuresModalTask(null)}
            />

            <RunLarsStagesModal
                isOpen={larsRunModalOpen}
                title="Select stages to run"
                defaultStages={
                    Array.isArray(pendingLarsTask?.task_extra_data?.stages) && pendingLarsTask!.task_extra_data.stages.length
                        ? pendingLarsTask!.task_extra_data.stages.map((s: number) => Number(s))
                        : [1, 2, 3, 4]
                }
                isSubmitting={!!runningGlobalTaskId}
                onClose={() => {
                    if (runningGlobalTaskId) return;
                    setLarsRunModalOpen(false);
                    setPendingLarsTask(null);
                }}
                onConfirm={(stages) => {
                    if (!pendingLarsTask) return;
                    void executeGlobalTask(pendingLarsTask, stages);
                }}
            />

        </div>
    );
}
